import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {useConfirmProduction} from '../../../hooks/productions/useProductions';
import {
  ConfirmProductionRequestParams,
  ConfirmProductionRequestBody,
} from '../../../api/types/production.type';
import '../Demo.scss';

const ConfirmProductionApiTest: React.FC = () => {
  // Query Parameters
  const [productionId, setProductionId] = useState<number>(59);
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [productionLabel, setProductionLabel] = useState<string>('');
  const [isCommit, setIsCommit] = useState<boolean>(true);

  // Request Body - Basic Info
  const [bodyProductionId, setBodyProductionId] = useState<number>(59);
  const [moduleType, setModuleType] = useState<string>('PRODUCTION');
  const [isMarkComplete, setIsMarkComplete] = useState<boolean>(false);
  const [shouldContinue, setShouldContinue] = useState<boolean>(false);
  const [canGenerate, setCanGenerate] = useState<boolean>(false);
  const [canRegenerate, setCanRegenerate] = useState<boolean>(true);
  const [securityGroupIdForProduction, setSecurityGroupIdForProduction] =
    useState<number>(1);
  const [productionType, setProductionType] = useState<number>(1);
  const [productionStateCode, setProductionStateCode] =
    useState<string>('AFTERDATAGENFAILED');
  const [productionTempararyFolderPath, setProductionTempararyFolderPath] =
    useState<string>(
      '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\destinationlocation\\dndproject\\CADE5C9F-B105-4A5A-ACA2-AFBD3096E55E\\Temp\\Production59'
    );
  const [productionDestinationFolderPath, setProductionDestinationFolderPath] =
    useState<string>(
      '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\Productions\\dndproject3\\pprod'
    );
  const [productionFolderPath, setProductionFolderPath] = useState<string>(
    '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\destinationlocation\\dndproject\\CADE5C9F-B105-4A5A-ACA2-AFBD3096E55E\\57377E09-5AA8-48EC-97D2-3652B8D7B34D'
  );
  const [shouldPerformBlankPageDetection, setShouldPerformBlankPageDetection] =
    useState<boolean>(false);
  const [regenerateType, setRegenerateType] = useState<number>(0);

  const [confirmResult, setConfirmResult] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const confirmMutation = useConfirmProduction(
    (data) => {
      setConfirmResult(`✅ ${data.message}`);
    },
    (error) => {
      setConfirmResult(`❌ Confirm failed: ${error.message}`);
    }
  );

  const handleConfirm = () => {
    const params: ConfirmProductionRequestParams = {
      ProductionId: productionId,
      SecurityGroupId: securityGroupId,
      ProductionLabel: productionLabel,
      IsCommit: isCommit,
    };
    const body: ConfirmProductionRequestBody = {
      ProductionId: bodyProductionId,
      ModuleType: moduleType,
      IsMarkComplete: isMarkComplete,
      ShouldContinue: shouldContinue,
      CanGenerate: canGenerate,
      CanRegenerate: canRegenerate,
      SecurityGroupIdForProduction: securityGroupIdForProduction,
      ProductionType: productionType,
      ProductionStateCode: productionStateCode,
      ProductionTempararyFolderPath: productionTempararyFolderPath,
      ProductionDestinationFolderPath: productionDestinationFolderPath,
      ProductionFolderPath: productionFolderPath,
      ShouldPerformBlankPageDetection: shouldPerformBlankPageDetection,
      RegenerateType: regenerateType,
    };
    confirmMutation.mutate({params, body});
  };

  const handleReset = () => {
    setConfirmResult('');
  };

  const handleCopyJson = async () => {
    if (confirmMutation.data) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(confirmMutation.data, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Confirm Production API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Commit or uncommit a production. Starts the commit/uncommit action as
          a background task.
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="POST" color="success" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Confirm Production (Commit/Uncommit)
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/confirm
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Commit or uncommit a production. POST request with query parameters
            (ProductionId, SecurityGroupId, ProductionLabel, IsCommit) and
            request body containing production details. Returns a message
            indicating that the action has started.
          </Typography>
        </CardContent>
      </Card>
      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 3,
            }}
          >
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Security Group ID"
              type="number"
              value={securityGroupId}
              onChange={(e) => setSecurityGroupId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Production Label"
              value={productionLabel}
              onChange={(e) => setProductionLabel(e.target.value)}
              size="small"
              placeholder="Leave empty if not needed"
            />
            <FormControlLabel
            data-testid="confirm-production-api-test-is-commit-form-control-label-commit"
              control={
                <Checkbox
                  data-testid="confirm-production-api-test-is-commit"
                  checked={isCommit}
                  onChange={(e) => setIsCommit(e.target.checked)}
                />
              }
              label="Is Commit (true = Commit, false = Uncommit)"
            />
          </Box>

          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
            sx={{marginTop: 2}}
          >
            Request Body
          </Typography>

          {/* Basic Information */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production ID"
                  type="number"
                  value={bodyProductionId}
                  onChange={(e) => setBodyProductionId(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Module Type"
                  value={moduleType}
                  onChange={(e) => setModuleType(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Security Group ID For Production"
                  type="number"
                  value={securityGroupIdForProduction}
                  onChange={(e) =>
                    setSecurityGroupIdForProduction(Number(e.target.value))
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production Type"
                  type="number"
                  value={productionType}
                  onChange={(e) => setProductionType(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production State Code"
                  value={productionStateCode}
                  onChange={(e) => setProductionStateCode(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Regenerate Type"
                  type="number"
                  value={regenerateType}
                  onChange={(e) => setRegenerateType(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          {/* Flags */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Flags
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="confirm-production-api-test-is-mark-complete-control-label-commit"
                  control={
                    <Checkbox
                      data-testid="confirm-production-api-test-is-mark-complete"
                      checked={isMarkComplete}
                      onChange={(e) => setIsMarkComplete(e.target.checked)}
                    />
                  }
                  label="Is Mark Complete"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="confirm-production-api-test-should-continue-form-control-label"
                  control={
                    <Checkbox
                      data-testid="confirm-production-api-test-should-continue"
                      checked={shouldContinue}
                      onChange={(e) => setShouldContinue(e.target.checked)}
                    />
                  }
                  label="Should Continue"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="confirm-production-api-test-can-generate-form-control-label"
                  control={
                    <Checkbox
                      data-testid="confirm-production-api-test-can-generate"
                      checked={canGenerate}
                      onChange={(e) => setCanGenerate(e.target.checked)}
                    />
                  }
                  label="Can Generate"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="confirm-production-api-test-can-regenerate-form-control-label"
                  control={
                    <Checkbox
                      data-testid="confirm-production-api-test-can-regenerate"
                      checked={canRegenerate}
                      onChange={(e) => setCanRegenerate(e.target.checked)}
                    />
                  }
                  label="Can Regenerate"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="confirm-production-api-test-should-perform-blank-page-detection-form-control-label"
                  control={
                    <Checkbox
                      data-testid="confirm-production-api-test-should-perform-blank-page-detection"
                      checked={shouldPerformBlankPageDetection}
                      onChange={(e) =>
                        setShouldPerformBlankPageDetection(e.target.checked)
                      }
                    />
                  }
                  label="Should Perform Blank Page Detection"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Folder Paths */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Folder Paths
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Production Temporary Folder Path"
                  value={productionTempararyFolderPath}
                  onChange={(e) =>
                    setProductionTempararyFolderPath(e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Production Destination Folder Path"
                  value={productionDestinationFolderPath}
                  onChange={(e) =>
                    setProductionDestinationFolderPath(e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Production Folder Path"
                  value={productionFolderPath}
                  onChange={(e) => setProductionFolderPath(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={confirmMutation.isPending}
              data-testid="confirm-production-confirm-button"
            >
              {confirmMutation.isPending
                ? '⏳ Processing...'
                : isCommit
                  ? '✅ Commit Production'
                  : '❌ Uncommit Production'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              data-testid="confirm-production-api-test-reset-button">
              🔄 Reset
            </Button>
            {confirmMutation.data && (
              <Button
                variant="outlined"
                onClick={handleCopyJson}
                data-testid="confirm-production-copy-button"
              >
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Loading State */}
      {confirmMutation.isPending && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ {isCommit ? 'Committing' : 'Uncommitting'} production... Please
              wait...
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Error State */}
      {confirmMutation.isError && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {confirmMutation.error?.message ||
                  'An error occurred while confirming production'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Success State */}
      {confirmMutation.isSuccess && confirmMutation.data && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Confirm Result
              </Typography>

              <Alert severity="success" sx={{marginBottom: 2}}>
                <Typography className="typo-text subhead3_size" gutterBottom>
                  ✅ Success
                </Typography>
                <Typography className="typo-text body3_size">
                  {confirmMutation.data.message}
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(confirmMutation.data, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
      {/* Result Display */}
      {confirmResult && !confirmMutation.isPending && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Confirm Result
            </Typography>
            <Alert
              severity={confirmResult.includes('✅') ? 'success' : 'error'}
            >
              {confirmResult}
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ConfirmProductionApiTest;
